// Store & Warehouse → "Requests" (kitchen flow, lane C): what the kitchen has
// asked for and what it has sent back, soonest needed first, updating live.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { getStoredUserRole } from '@/lib/staff-roles'
import { pollInterval, useRequisitionStream, type RequisitionStreamStatus } from '@/lib/inventoryFlow/requisitionStream'
import { storeNeedsAction } from '@/lib/inventoryFlow/storeRequestsLogic'
import { sortByNeededBy } from '@/lib/inventoryFlow/storeScreenLogic'
import { ST } from '@/lib/inventoryFlow/storeScreenStrings'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { LiveDot, StoreTodoList } from './RequisitionList'
import { RequisitionPanel } from './RequisitionPanel'

type Filter = 'todo' | 'open' | 'all'

export function StoreRequestsTab({ showToast, onThrowAway }: {
  showToast: (type: 'success' | 'error', message: string) => void
  /** Opens where expired stock is written off (Alerts). */
  onThrowAway?: () => void
}) {
  const role = getStoredUserRole()
  const isAdmin = role === 'admin'
  const qc = useQueryClient()
  const [selected, setSelected] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter>('todo')
  const refetch = useCallback(() => void qc.invalidateQueries({ queryKey: ['inventory-flow'] }), [qc])
  const live = useRequisitionStream({ enabled: true, onEvent: refetch, onReconnect: refetch })

  const { data, isLoading, isError, refetch: retry, isFetching } = useQuery({
    queryKey: ['inventory-flow', 'requisitions', 'all'],
    // Every open request however old (To do is worked out from these), plus the newest 200.
    queryFn: () => apiClient.getRequisitions({ limit: 200, include_open: true }),
    refetchInterval: pollInterval(live),
  })
  const all = useMemo(() => data?.data ?? [], [data])
  const todo = useMemo(() => sortByNeededBy(all.filter(storeNeedsAction)), [all])
  const shown = useMemo(() => {
    if (filter === 'todo') return todo
    // Open: still moving, soonest needed first. All: newest first, as before.
    if (filter === 'open') return sortByNeededBy(all.filter((r) => !['closed', 'rejected', 'cancelled', 'draft'].includes(r.status)))
    return all.filter((r) => r.status !== 'draft')
  }, [all, todo, filter])

  // Open the first request in the list, so the store's next job is on screen.
  useEffect(() => {
    if (!selected && filter === 'todo' && todo.length > 0) setSelected(todo[0].id)
  }, [selected, filter, todo])

  // The To do count is the server's (summary.store_todo), like the tab badge.
  const todoCount = useStoreRequestsBadge(true, live)
  const filters: { id: Filter; label: string; count?: number }[] = [
    { id: 'todo', label: ST.todo.filterNow, count: todoCount || todo.length },
    { id: 'open', label: ST.todo.filterOpen },
    { id: 'all', label: ST.todo.filterAll },
  ]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1 rounded-lg bg-muted/60 p-1" role="group" aria-label="Show">
          {filters.map((f) => (
            <button key={f.id} type="button" onClick={() => setFilter(f.id)}
              className={cn('flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                filter === f.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
              {f.label}
              {!!f.count && <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">{f.count}</span>}
            </button>
          ))}
        </div>
        <LiveDot status={live} />
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(18rem,24rem)_minmax(0,1fr)]">
        <div className="min-w-0">
          {isLoading ? (
            <div className="space-y-2" aria-busy="true">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full rounded-lg" />)}
            </div>
          ) : isError ? (
            <div className="space-y-3 rounded-lg border border-dashed p-6 text-center text-sm">
              <p className="text-muted-foreground">{ST.todo.loadError}</p>
              <Button variant="outline" size="sm" disabled={isFetching} onClick={() => void retry()}>{ST.todo.retry}</Button>
            </div>
          ) : (
            <StoreTodoList items={shown} selectedId={selected} onSelect={setSelected} isAdmin={isAdmin}
              header={filter === 'all' ? undefined : ST.todo.sortedBy}
              emptyText={filter === 'todo' ? ST.todo.emptyTodo : ST.todo.emptyList} />
          )}
        </div>
        <div className="min-w-0 rounded-lg border bg-card p-4">
          {/* key: a fresh panel per request, so a half-typed reject reason or
              write-off PIN never carries over to the next one. */}
          {selected
            ? <RequisitionPanel key={selected} id={selected} role={role} notify={showToast} storeView onThrowAway={onThrowAway} />
            : <p className="text-sm text-muted-foreground">{ST.todo.pick}</p>}
        </div>
      </div>
    </div>
  )
}

/** Count for the tab badge — shares the list query's cache key family. */
export function useStoreRequestsBadge(enabled: boolean, live?: RequisitionStreamStatus): number {
  const { data } = useQuery({
    queryKey: ['inventory-flow', 'summary'],
    queryFn: () => apiClient.getRequisitionSummary(),
    enabled,
    refetchInterval: live ? pollInterval(live) : 60_000,
  })
  const s = data?.data
  if (!s) return 0
  // store_todo counts each request once, missing stock included; the sum is
  // the fallback for a backend from before it existed.
  return s.store_todo ?? s.awaiting_approval + s.awaiting_issue + s.returns_awaiting_receipt
}
